import React, { useState, useCallback } from "react";
import { Box, Paper, Typography, Button, alpha } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { brandThemeOptions } from "./brandColors";
import {
  Dialog,
  DialogTitle,
  Drawer,
  DialogContent,
  IconButton,
} from "@mui/material";
import { CheckCircleOutlineOutlined, CloseOutlined } from "@mui/icons-material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";
import { IPolicyDriver } from "../../../models/user/policyDetails/PolicyDetails";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { formatDateToYYYYMMDD } from "../../../utils/Utility";
import AlertDialog from "../../../components/AlertDialog/AlertDialog";
import MessageDialog from "../../../components/MessageDialog/MessageDialog";
import { setCalledReducerType } from "../../../store/common/CommonSlice";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface DriverDetailsCardProps {
    card: any;
    // onAction?: (action: PolicyAction) => void;
  onAction?: (action: { followup_message?: string; label?: string; params?: any; }) => void;
 
}

const DriverDetailsCard: React.FC<DriverDetailsCardProps> = ({ card, onAction }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // Redux state
    const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
    const { policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);
    const { isSuccess, isError, calledReducerType } = useAppSelector((state: RootState) => state.common);

    // Local state for dialogs
    const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
    const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
    const [pendingDeleteAction, setPendingDeleteAction] = useState<PolicyAction | null>(null);
    const [pendingFollowupMessage, setPendingFollowupMessage] = useState<string | undefined>(undefined);

    // Extract data safely (before early returns)
    const driverSection = card?.sections?.[0];
    const driverItems = driverSection?.items || [];
    const refs = card?.entity_refs || {};
    const policyId = refs.policy_id || card?.policyId;
    const carrier = refs.carrier || card?.carrier;
    const lobCode = refs.lob_code || card?.lobCode;
    const system = refs.system || card?.system;
    const policyNumber = refs.policy_number || card?.policyNumber;

    const buildDriverData = useCallback(
        (driverItemData: any): Partial<IPolicyDriver> => {
            const data: Partial<IPolicyDriver> = {};
            const derivedName = driverItemData?.Name || refs.driver_name;
            const derivedLicense = driverItemData?.License || refs.driver_license;
            const derivedGender = driverItemData?.Gender || refs.driver_gender;
            const derivedMaritalStatus = driverItemData?.["Marital Status"] || refs.driver_marital_status;
            const derivedAge = driverItemData?.Age || refs.driver_age;
            const derivedType = driverItemData?.Type || refs.driver_type;

            if (derivedName) {
                const nameParts = derivedName.trim().split(/\s+/);
                if (nameParts.length >= 2) {
                    data.givenName = nameParts[0];
                    data.surName = nameParts[nameParts.length - 1];
                    if (nameParts.length > 2) {
                        data.otherGivenName = nameParts.slice(1, -1).join(" ");
                    }
                } else if (nameParts.length === 1) {
                    data.givenName = nameParts[0];
                }
            }

            if (derivedLicense) {
                const licenseMatch = derivedLicense.match(/^([A-Z]{2})[- ]?(.+)$/);
                if (licenseMatch) {
                    data.licensedStateProvCd = licenseMatch[1];
                    data.licenseNumber = licenseMatch[2];
                } else {
                    data.licenseNumber = derivedLicense;
                }
            }

            if (derivedGender) {
                data.genderCd = derivedGender;
            }
            if (derivedMaritalStatus) {
                data.maritalStatus = derivedMaritalStatus;
            }
            if (derivedAge) {
                data.driverAge = typeof derivedAge === "number" ? derivedAge : parseInt(derivedAge, 10);
            }
            if (derivedType) {
                data.typeOfDriver = derivedType;
            }

            if (refs.driver_given_name) data.givenName = refs.driver_given_name;
            if (refs.driver_surname) data.surName = refs.driver_surname;
            if (refs.driver_license_state) data.licensedStateProvCd = refs.driver_license_state;
            if (refs.driver_license_number) data.licenseNumber = refs.driver_license_number;
            if (refs.driver_date_of_birth) data.dateOfBirth = refs.driver_date_of_birth;
            if (refs.driver_license_date) data.licenseDt = refs.driver_license_date;
            if (refs.driver_relationship) data.relationshipToInsuredCd = refs.driver_relationship;

            return data;
        },
        [refs]
    );

    // Show message dialog when remove driver action completes
    // useEffect(() => {
    //     if (
    //         (isSuccess || isError) &&
    //         calledReducerType === "policyChange/postRemoveDriver"
    //     ) {
    //         setMessageDialogOpen(true);
    //     }
    // }, [isSuccess, isError, calledReducerType]);

    const handleDeleteClick = useCallback((action: PolicyAction | undefined) => {
        if (!action) return;
        setPendingDeleteAction(action);
        setPendingFollowupMessage(action.followup_message);
        setAlertDialogOpen(true);
    }, []);

    const handleAlertDialogCancelClick = useCallback(() => {
        setAlertDialogOpen(false);
    }, []);

    const handleAlertDialogOkClick = useCallback(() => {
        setAlertDialogOpen(false);
        // if (pendingDeleteAction) {
        //     onAction?.(pendingDeleteAction);
        //     if (pendingDeleteAction.followup_message) {
        //         onAction?.({ followup_message: pendingDeleteAction.followup_message });
        //     }
        // }
        // setPendingDeleteAction(null);
        setMessageDialogOpen(true);
        const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
        if (pn && onAction) {
          onAction({ followup_message: `Show me details for policy ${pn}` });
        }
    }, [pendingDeleteAction, onAction]);

    const handleMessageDialogClose = useCallback(() => {
        setMessageDialogOpen(false);
        dispatch(setCalledReducerType(null));
      }, [dispatch]);

    // Early returns after all hooks
    if (!card) return null;
    if (!driverItems.length) return null;

    const handleAction = (action: PolicyAction | undefined, driverItemData: any) => {
        if (!action) return;
        
        if (action.id.includes("delete") || action.id.includes("remove")) {
            handleDeleteClick(action);
            return;
        }
        
        if (action.id === "start_intent" || action.id.includes("edit") || action.id.includes("update")) {
            const editDriverCard = {
                type: "edit_driver",
                title: "Update Driver",
                entity_refs: {
                    policy_id: policyId,
                    carrier: carrier,
                    lob_code: lobCode,
                    system: system,
                    policy_number: driverItemData?.Policy || policyNumber,
                },
                driver_data: buildDriverData(driverItemData),
            };

            onAction?.({
                ...action,
                params: {
                    ...action.params,
                    card: editDriverCard,
                },
            });
        } else {
            onAction?.(action);
        }
    };


    const getInitials = (name: string) => {
        const parts = (name || "").trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return "?";
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const getActionIcon = (actionId: string) => {
        if (actionId === "start_intent" || actionId.includes("edit") || actionId.includes("update")) {
            return <EditOutlinedIcon sx={{ fontSize: 14 }} />;
        }
        if (actionId.includes("delete") || actionId.includes("remove")) {
            return <DeleteOutlinedIcon sx={{ fontSize: 14 }} />;
        }
        return null;
    };

    return (
        <ThemeProvider theme={(outer) => createTheme(outer, brandThemeOptions)}>
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #eceff3",
                borderRadius: { xs: 2, md: 2.5 },
                p: { xs: 1.25, md: 1.75 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                        sx={{
                            width: { xs: 28, md: 32 },
                            height: { xs: 28, md: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            flexShrink: 0,
                        }}
                    >
                        <PersonOutlinedIcon sx={{ fontSize: { xs: 17, md: 19 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}>
                            {card?.title || "Driver Details"}
                        </Typography>
                        {card?.subtitle && (
                            <Typography sx={{ fontSize: { xs: 10.5, md: 12 }, color: "#6b7280" }}>
                                {card.subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {driverItems.map((driverItem: any, idx:any) => {
                        const driverName = driverItem?.Name || `Driver ${idx + 1}`;
                        const driverActions = driverItem?.actions || [];
                        const infoFields = [
                            { label: "License", value: driverItem?.License },
                            { label: "Type", value: driverItem?.Type },
                            { label: "Gender", value: driverItem?.Gender },
                            { label: "Age", value: driverItem?.Age },
                            { label: "Marital Status", value: driverItem?.["Marital Status"] },
                            { label: "Policy", value: driverItem?.Policy || policyNumber },
                        ].filter(field => field.value);

                        return (
                            <Box
                                key={`${driverItem?.License || driverItem?.Name || idx}`}
                                sx={{
                                    position: "relative",
                                    overflow: "hidden",
                                    border: "1px solid #eceff3",
                                    borderRadius: { xs: 2, md: 2.5 },
                                    bgcolor: "#ffffff",
                                    transition:
                                        "box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease",
                                    "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow: (theme) =>
                                            `0 8px 22px ${alpha(theme.palette.primary.main, 0.14)}`,
                                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        p: { xs: 1.25, md: 1.5 },
                                        background: (theme) =>
                                            `linear-gradient(135deg, ${alpha(
                                                theme.palette.primary.main,
                                                0.1
                                            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                        borderBottom: "1px solid",
                                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 36, md: 42 },
                                                height: { xs: 36, md: 42 },
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                                color: "primary.contrastText",
                                                bgcolor: "primary.main",
                                                fontWeight: 700,
                                                fontSize: { xs: 13, md: 15 },
                                                boxShadow: (theme) =>
                                                    `0 4px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
                                            }}
                                        >
                                            {getInitials(driverName)}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: { xs: 14, md: 16 },
                                                    color: "#111827",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {driverName}
                                            </Typography>
                                            {driverItem?.Type && (
                                                <Typography sx={{ fontSize: { xs: 10.5, md: 12 }, color: "primary.main", fontWeight: 600 }}>
                                                    {driverItem.Type}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    {driverActions.length > 0 && (
                                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                            {driverActions.map((action: PolicyAction, actionIdx: number) => {
                                                const isDelete = action.id.includes("delete") || action.id.includes("remove");
                                                return (
                                                <Button
                                                    key={`action_${idx}_${actionIdx}`}
                                                    size="small"
                                                    variant={isDelete ? "outlined" : "contained"}
                                                    color={isDelete ? "error" : "primary"}
                                                    disableElevation
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAction(action, driverItem);
                                                    }}
                                                    startIcon={getActionIcon(action.id)}
                                                    sx={{
                                                        fontSize: { xs: 10, md: 12 },
                                                        fontWeight: 600,
                                                        py: { xs: 0.4, md: 0.6 },
                                                        px: { xs: 1.25, md: 1.5 },
                                                        borderRadius: 9999,
                                                        textTransform: "none",
                                                        whiteSpace: "nowrap",
                                                        ...(isDelete && {
                                                            "&:hover": {
                                                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                                                                borderColor: "error.main",
                                                                color: "error.main",
                                                            },
                                                        }),
                                                    }}
                                                >
                                                    {action.label}
                                                </Button>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                        gap: { xs: 1, md: 1.25 },
                                        p: { xs: 1.25, md: 1.5 },
                                    }}
                                >
                                    {infoFields.map((field, infoIdx) => (
                                        <Box
                                            key={`driver_field_${idx}_${infoIdx}`}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.15,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: { xs: 9.5, md: 10.5 },
                                                    color: "#9ca3af",
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.4,
                                                }}
                                            >
                                                {field.label}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: 12, md: 13 },
                                                    color: "#111827",
                                                    fontWeight: 600,
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {field.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {(card?.disclaimers || []).length > 0 && (
                    <Typography
                        sx={{ fontSize: { xs: 10, md: 11 }, color: "#6b7280", mt: 0.5 }}
                    >
                        {card.disclaimers.join(" ")}
                    </Typography>
                )}
            </Box>
        </Paper>
        <AlertDialog
            handleCancelClick={handleAlertDialogCancelClick}
            handleOkClick={handleAlertDialogOkClick}
            open={alertDialogOpen}
            title={t("Removing this driver will end its coverage under this policy")}
        />
        <Dialog open={messageDialogOpen} sx={{ backdropFilter: "blur(5px)" }}>
                      <DialogTitle
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <IconButton 
                        onClick={handleMessageDialogClose}
                        >
                          <CloseOutlined />
                        </IconButton>
                      </DialogTitle>
              
                      <DialogContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <CheckCircleOutlineOutlined
                            sx={{ fontSize: "60px", color: "green" }}
                          />
                          <Typography>{t("Policy Change has been initiated")}</Typography>
                        </Box>
                      </DialogContent>
                    </Dialog>
    </ThemeProvider>
    );
};

export default DriverDetailsCard;

